import { useEffect, useState } from "react";
import axios from "axios";
import { Tabs, Tab, Form, Row, Col, Button, Table, Badge } from "react-bootstrap";
import ReactQuill from "react-quill";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";
import { uploadImagesBlogApiRequest } from "../utils/utils";

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["link"],
        ["clean"],
    ],
};
const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "color",
    "background",
    "align",
    "link",
];

function htmlToText(html) {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
}

const AdminBlogPageComponent = ({ userInfo }) => {
    const [activeKey, setActiveKey] = useState("create");

    // create/edit form state
    const [images, setImages] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [newBlog, setNewBlog] = useState({
        titleHtml: "",
        plainTitle: "",
        descriptionHtml: "",
        category: "",
        heroImage: "", // keep existing hero when editing
        published: false,
    });
    const [submitting, setSubmitting] = useState(false);

    // manage list state
    const [list, setList] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState("");

    const fetchBlogs = async () => {
        try {
            setListLoading(true);
            setListError("");
            const { data } = await axios.get("/api/blogs?limit=200"); // include drafts for admin list
            const items = Array.isArray(data) ? data : data.items || [];
            setList(items);
        } catch (e) {
            console.error(e);
            setListError("Failed to load blogs.");
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        // Always fetch when you land on Manage tab
        if (activeKey === "manage") fetchBlogs();
    }, [activeKey]);

    const uploadHandlerImage = (e) => setImages(e.target.files);

    const displayImages = () =>
        images &&
        Array.from(images).map((image, index) => (
            <img
                src={URL.createObjectURL(image)}
                key={index}
                alt="Selected"
                style={{ margin: "2px", width: "50%", height: "auto" }}
            />
        ));

    const resetForm = () => {
        setEditingId(null);
        setImages([]);
        setNewBlog({
            titleHtml: "",
            plainTitle: "",
            descriptionHtml: "",
            category: "",
            heroImage: "",
            published: false,
        });
    };

    const assignBlog = async () => {
        try {
            setSubmitting(true);

            // sanitise
            const safeTitleHtml = DOMPurify.sanitize(newBlog.titleHtml);
            const safeBodyHtml = DOMPurify.sanitize(newBlog.descriptionHtml);

            // plain title for slug/SEO
            const plainTitle =
                newBlog.plainTitle?.trim() || htmlToText(safeTitleHtml).trim() || "Untitled";

            // excerpt + reading time
            const bodyText = htmlToText(safeBodyHtml).trim();
            const excerpt = bodyText.slice(0, 180) + (bodyText.length > 180 ? "…" : "");
            const readingMinutes = Math.max(2, Math.round(bodyText.split(/\s+/).length / 220));

            // hero image
            let imageUrl = newBlog.heroImage || "";
            if (images.length > 0) {
                // your uploader (assumed async)
                await uploadImagesBlogApiRequest(images);
                const imageName = images[0].name.trim()
                    .toLowerCase()
                    .replace(/[\s_]+/g, "-")
                    .replace(/[^a-z0-9.-]/g, "")
                    .replace(/-+/g, "-");

                imageUrl = `https://ctladmin.b-cdn.net/blog/${imageName} `;
            }

            const payload = {
                title: plainTitle,
                titleHtml: safeTitleHtml,
                excerpt,
                body: safeBodyHtml,
                category: newBlog.category || "General",
                tags: [],
                heroImage: imageUrl,
                readingMinutes,
                published: newBlog.published,
                publishedAt: newBlog.published ? new Date() : null,
                author: { name: userInfo?.name || "CTL Australia" },
            };

            if (editingId) {
                // UPDATE
                await axios.put(`/api/blogs/admin/update/${editingId} `, payload);
                alert("Blog updated.");
            } else {
                // CREATE
                await axios.post("/api/blogs/admin/create", payload);
                alert(newBlog.published ? "Blog published." : "Blog created (draft).");
            }

            resetForm();
            // if you’re on create tab after saving, jump to Manage to see it
            setActiveKey("manage");
            fetchBlogs();
        } catch (err) {
            console.error(err);
            alert("Failed to save blog.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (post) => {
        console.log(post);

        setEditingId(post._id);
        setNewBlog({
            titleHtml: post.titleHtml || post.title || "",
            plainTitle: post.title || "",
            descriptionHtml: post.body || "",
            category: post.category || "",
            heroImage: post.heroImage || "",
            published: !!post.published,
        });
        setImages([]);
        setActiveKey("create");
        // optional: scroll to top of form
        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this blog permanently?")) return;
        try {
            await axios.delete(`/ api / blogs / admin / delete/${id}`);
            setList((prev) => prev.filter((b) => b._id !== id));
        } catch (e) {
            console.error(e);
            alert("Failed to delete blog.");
        }
    };

    return (
        <>
            <Tabs activeKey={activeKey} onSelect={(k) => setActiveKey(k || "create")} mountOnEnter={false}
                unmountOnExit={false} className="mb-3">
                <Tab eventKey="create" title={editingId ? "Edit Blog" : "Create Blog"}>
                    <h5>{editingId ? "Edit Blog" : "Create New Blog"}</h5>
                    <Form
                        className="p-4 mb-4 bg-light rounded shadow-sm border"
                        onSubmit={(e) => {
                            e.preventDefault();
                            assignBlog();
                        }}
                    >
                        <Row className="mb-3">
                            <Col md={12} className="mb-3">
                                <Form.Label><strong>Title (Rich)</strong></Form.Label>
                                <ReactQuill
                                    key={`title-${editingId ?? 'new'}`}
                                    theme="snow"
                                    modules={quillModules}
                                    formats={quillFormats}
                                    value={newBlog.titleHtml}
                                    onChange={(val) => setNewBlog({ ...newBlog, titleHtml: val })}
                                    placeholder="Write your headline…"
                                />
                                <Form.Text muted>We’ll also store a plain title for the URL/slug.</Form.Text>
                            </Col>

                            <Col md={12} className="mb-3">
                                <Form.Label><strong>Plain Title (for slug & SEO)</strong></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Short, clean, SEO-friendly title (no formatting)"
                                    value={newBlog.plainTitle}
                                    onChange={(e) => setNewBlog({ ...newBlog, plainTitle: e.target.value })}
                                />
                            </Col>

                            <Col md={12} className="mb-3">
                                <Form.Label><strong>Description / Body</strong></Form.Label>
                                <ReactQuill
                                    key={`body-${editingId ?? 'new'}`}
                                    theme="snow"
                                    modules={quillModules}
                                    formats={quillFormats}
                                    value={newBlog.descriptionHtml}
                                    onChange={(val) => setNewBlog({ ...newBlog, descriptionHtml: val })}
                                    placeholder="Write the full article content…"
                                />
                            </Col>

                            <Col md={12} className="mb-3">
                                <Form.Label><strong>Hero Image</strong></Form.Label>
                                <Form.Control
                                    type="file"
                                    onChange={uploadHandlerImage}
                                />
                                {newBlog.heroImage && images.length === 0 && (
                                    <div className="mt-2">
                                        <img
                                            src={newBlog.heroImage}
                                            alt="Current hero"
                                            style={{ maxWidth: 300, borderRadius: 8 }}
                                        />
                                    </div>
                                )}
                                <div style={{ display: "flex", flexWrap: "wrap", maxWidth: "900px" }}>
                                    {displayImages()}
                                </div>
                            </Col>

                            <Col md={3} className="text-start d-flex flex-column mt-2">
                                <Form.Label><strong>Category</strong></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Type Category Name"
                                    value={newBlog.category}
                                    onChange={(e) => setNewBlog({ ...newBlog, category: e.target.value })}
                                />

                                <Form.Check
                                    type="checkbox"
                                    id="publishCheck"
                                    label="Publish now"
                                    checked={newBlog.published || false}
                                    onChange={(e) => setNewBlog({ ...newBlog, published: e.target.checked })}
                                    className="mb-3 mt-3"
                                />

                                <div className="d-flex gap-2">
                                    <Button variant="primary" onClick={assignBlog} disabled={submitting}>
                                        {submitting ? (editingId ? "Updating…" : "Creating…") : (editingId ? "Update Blog" : "Create Blog")}
                                    </Button>
                                    {editingId && (
                                        <Button variant="outline-secondary" onClick={resetForm}>
                                            Cancel Edit
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Tab>

                <Tab eventKey="manage" title="Manage Blogs">
                    <div className="p-3 bg-light rounded border">
                        {listLoading ? (
                            <div>Loading…</div>
                        ) : listError ? (
                            <div className="text-danger">{listError}</div>
                        ) : (
                            <Table hover responsive className="align-middle">
                                <thead>
                                    <tr>
                                        <th>Post</th>
                                        <th>Status</th>
                                        <th>Updated</th>
                                        <th style={{ width: 160 }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.map((p) => (
                                        <tr key={p._id}>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    {p.heroImage ? (
                                                        <img src={p.heroImage} alt="" style={{ width: 64, height: 44, objectFit: "cover", borderRadius: 6 }} />
                                                    ) : (
                                                        <div style={{ width: 64, height: 44, background: "#e5e7eb", borderRadius: 6 }} />
                                                    )}
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{p.title}</div>
                                                        <div style={{ fontSize: 12, color: "#6b7280" }}>{p.slug}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {p.published ? (
                                                    <Badge bg="success">Published</Badge>
                                                ) : (
                                                    <Badge bg="secondary">Draft</Badge>
                                                )}
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : ""}
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button size="sm" variant="outline-primary" onClick={() => handleEdit(p)}>
                                                        Edit
                                                    </Button>
                                                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(p._id)}>
                                                        Delete
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {list.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted">
                                                No blogs yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        )}
                    </div>
                </Tab>
            </Tabs>
        </>
    );
};

export default AdminBlogPageComponent;
